/** PIZARRA INTELIGENTE SACH — 4ª modalidad de Presupuestos SA.
 *  Foto/dibujo a mano → interpretación IA (sin inventar datos) → pizarra interactiva
 *  → planilla + gastos → borrador SA con el motor existente. */
import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '@/api/axios';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Camera, FileArrowUp, Spinner, ArrowUUpLeft, ArrowUUpRight } from '@phosphor-icons/react';
import { LienzoPizarra } from '@/components/pizarra/LienzoPizarra';
import { ConfigElemento } from '@/components/pizarra/ConfigElemento';
import { PlanillaPizarra, GastosPizarra, subtotalElemento } from '@/components/pizarra/PlanillaPizarra';
import { PanelEditor } from '@/components/pizarra/PlanoInteligente';
import { BibliotecaPlanos } from '@/components/pizarra/BibliotecaPlanos';
import { TarifasServicios } from '@/components/pizarra/TarifasServicios';
import { useAuth } from '@/contexts/AuthContext';
import { derivarDiseno } from '@/components/sa/Modulador';

const redim = (file, max = 1600) => new Promise((res, rej) => {
  const img = new Image();
  img.onload = () => {
    const esc = Math.min(1, max / Math.max(img.width, img.height));
    const c = document.createElement('canvas');
    c.width = Math.round(img.width * esc); c.height = Math.round(img.height * esc);
    c.getContext('2d').drawImage(img, 0, 0, c.width, c.height);
    res(c.toDataURL('image/jpeg', 0.85));
  };
  img.onerror = rej;
  img.src = URL.createObjectURL(file);
});

const TIPO_IA = { bisagra: 'bisagra', clip: 'clip', jalador: 'jalador', cerradura: 'cerradura', conector: 'clip' };
const PREFIJO = { vidrio: 'V', puerta: 'P', bisagra: 'B', clip: 'C', jalador: 'J', perfil: 'PF', cerradura: 'CE', union: 'U', anotacion: 'N', otro: 'M' };
// Etiquetas comprensibles por tipo (V1, P1, B1…) — el id interno nunca cambia
const reetiquetar = (els) => { const n = {}; return els.map((e) => { const p = PREFIJO[e.tipo] || 'M'; n[p] = (n[p] || 0) + 1; return { ...e, etiqueta: `${p}${n[p]}` }; }); };
const TOOL_LABEL = { seleccionar: '👆 Seleccionar', mover: '✋ Mover', agregar: '➕ Agregando…', dibujar: '✏️ Trazo', medir: '📐 Medir' };

export default function PizarraSach() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [lista, setLista] = useState(null);
  const [piz, setPiz] = useState(null);           // pizarra activa
  const [analizando, setAnalizando] = useState(false);
  const [herramienta, setHerramienta] = useState('seleccionar');
  const [sel, setSel] = useState(null);
  const histRef = useRef({ snaps: [], idx: -1 });
  const [, setHistTick] = useState(0);
  const [autosave, setAutosave] = useState('guardado');
  const [reintento, setReintento] = useState(0);
  const savedRef = useRef('');
  const pizRef = useRef(null);
  const [guardando, setGuardando] = useState(false);
  const [generando, setGenerando] = useState(false);
  const [verTarifas, setVerTarifas] = useState(false);
  const [porColocar, setPorColocar] = useState(null);
  const [focus, setFocus] = useState(null);
  const [menu, setMenu] = useState(null);
  const [tipoNuevo, setTipoNuevo] = useState(null);
  const [verMateriales, setVerMateriales] = useState(false);
  const [verMarcadores, setVerMarcadores] = useState(true);
  const [previewOrg, setPreviewOrg] = useState(null);
  const [vista, setVista] = useState('foto');
  const [selPanel, setSelPanel] = useState(null);
  const [genPlano, setGenPlano] = useState(false);
  const [confirmRegen, setConfirmRegen] = useState(false);
  const [editDesc, setEditDesc] = useState(false);
  const [verBiblioteca, setVerBiblioteca] = useState(false);
  const [descTmp, setDescTmp] = useState('');
  const [descNueva, setDescNueva] = useState('');
  const [esEscritorio, setEsEscritorio] = useState(() => window.matchMedia('(min-width: 1024px)').matches);
  useEffect(() => {
    const m = window.matchMedia('(min-width: 1024px)');
    const f = (ev) => setEsEscritorio(ev.matches);
    m.addEventListener('change', f);
    return () => m.removeEventListener('change', f);
  }, []);
  const fileRef = useRef(null);
  const camRef = useRef(null);
  pizRef.current = piz;

  const cargarLista = () => api.get('/sa/pizarra').then((r) => setLista(r.data.pizarras || [])).catch(() => setLista([]));
  useEffect(() => { cargarLista(); }, []);

  // ── Historial deshacer/rehacer (elementos + trazos + mediciones, incluye mover marcadores) ──
  const snapDe = (p) => JSON.stringify({ elementos: p.elementos || [], trazos: p.trazos || [], mediciones: p.mediciones || [], plano: p.plano || null });
  const resetHist = (p) => { histRef.current = { snaps: [snapDe(p)], idx: 0 }; setHistTick((t) => t + 1); };
  const commit = (p) => {
    const h = histRef.current;
    const s = snapDe(p);
    if (h.idx >= 0 && s === h.snaps[h.idx]) return;
    h.snaps = [...h.snaps.slice(0, h.idx + 1), s].slice(-40);
    h.idx = h.snaps.length - 1;
    setHistTick((t) => t + 1);
  };
  const mutar = (patch) => { const nuevo = { ...pizRef.current, ...patch }; setPiz(nuevo); commit(nuevo); };
  const moverHist = (dir) => {
    const h = histRef.current;
    const ni = h.idx + dir;
    if (ni < 0 || ni >= h.snaps.length) return;
    h.idx = ni;
    setPiz({ ...pizRef.current, ...JSON.parse(h.snaps[ni]) });
    setHistTick((t) => t + 1);
  };
  const puedeDeshacer = histRef.current.idx > 0;
  const puedeRehacer = histRef.current.idx >= 0 && histRef.current.idx < histRef.current.snaps.length - 1;

  // ── Autosave: debounce 2.5s, reintento ante error, flush al ocultar/cerrar la pestaña ──
  const bodyAuto = (p) => { const { id, vendor_id, vendor_name, created_at, updated_at, ...body } = p; return { ...body, imagen: '' }; };
  useEffect(() => {
    if (!piz?.id) return;
    const s = JSON.stringify(bodyAuto(piz));
    if (s === savedRef.current) { setAutosave('guardado'); return; }
    setAutosave('pendiente');
    const t = setTimeout(async () => {
      setAutosave('guardando');
      try {
        await api.put(`/sa/pizarra/${piz.id}`, JSON.parse(s));
        savedRef.current = s;
        setAutosave('guardado');
      } catch { setAutosave('error'); }
    }, 2500);
    return () => clearTimeout(t);
  }, [piz, reintento]); // eslint-disable-line
  useEffect(() => {
    if (autosave !== 'error') return;
    const t = setTimeout(() => setReintento((r) => r + 1), 8000);
    return () => clearTimeout(t);
  }, [autosave]);
  useEffect(() => {
    const flush = () => {
      const p = pizRef.current;
      if (!p?.id) return;
      const s = JSON.stringify(bodyAuto(p));
      if (s === savedRef.current) return;
      try {
        fetch(`${api.defaults.baseURL}/sa/pizarra/${p.id}`, { method: 'PUT', keepalive: true, credentials: 'include', headers: { 'Content-Type': 'application/json' }, body: s });
        savedRef.current = s;
      } catch { /* reintenta al volver */ }
    };
    const onVis = () => { if (document.visibilityState === 'hidden') flush(); };
    document.addEventListener('visibilitychange', onVis);
    window.addEventListener('pagehide', flush);
    return () => { document.removeEventListener('visibilitychange', onVis); window.removeEventListener('pagehide', flush); };
  }, []);

  const elegir = async (e) => {
    const f = e.target.files?.[0];
    e.target.value = '';
    if (!f) return;
    let imagen, mime = 'image/jpeg';
    if (f.type === 'application/pdf' || /\.pdf$/i.test(f.name)) {
      if (f.size > 8 * 1024 * 1024) return toast.error('PDF máximo 8MB');
      imagen = await new Promise((res) => { const fr = new FileReader(); fr.onload = () => res(fr.result); fr.readAsDataURL(f); });
      mime = 'application/pdf';
    } else if (/image\/(jpeg|png|webp)/.test(f.type) || /\.(jpe?g|png|webp)$/i.test(f.name)) {
      try { imagen = await redim(f); } catch { return toast.error('No se pudo leer la imagen'); }
    } else return toast.error('Usa JPG, PNG o PDF');

    setAnalizando(true);
    try {
      const { data } = await api.post('/sa/pizarra/analizar', { imagen_base64: imagen, mime, descripcion: descNueva }, { timeout: 200000 });
      const d = data.datos || {};
      if (d.es_dibujo === false) { toast.error(`No parece un dibujo/solicitud: ${d.motivo || 'revisa la foto'}`, { duration: 8000 }); setAnalizando(false); return; }
      let n = 0;
      const mk = (tipo, x, y, props, estado = 'pendiente') => {
        const sin = x == null || y == null || isNaN(Number(x)) || isNaN(Number(y));
        n += 1;
        return { id: Math.random().toString(36).slice(2, 10), tipo, sin_ubicar: sin,
          x: sin ? null : Math.min(Math.max(Number(x), 0.02), 0.98),
          y: sin ? null : Math.min(Math.max(Number(y), 0.02), 0.98),
          etiqueta: `M${n}`, estado, origen: 'ia', props, producto: null };
      };
      const elementos = reetiquetar([
        ...(d.paneles || []).map((p) => mk(p.tipo === 'puerta' ? 'puerta' : 'vidrio', p.x, p.y, {
          descripcion: p.descripcion || '', ancho_m: p.ancho_m?.estado !== 'ilegible' ? p.ancho_m?.valor : '',
          alto_m: p.alto_m?.estado !== 'ilegible' ? p.alto_m?.valor : '',
          tipo_vidrio: d.vidrio?.tipo?.valor || '', espesor: d.vidrio?.espesor?.valor || '', color: d.vidrio?.color?.valor || '',
          apertura: p.apertura?.valor || '', notas: `${p.notas || ''}${p.ancho_m?.estado === 'interpretado' || p.alto_m?.estado === 'interpretado' ? ' · ⚠ medidas interpretadas — confírmalas' : ''}`.trim(),
        })),
        ...(d.herrajes || []).map((h) => mk(TIPO_IA[h.tipo] || 'otro', h.x, h.y, { descripcion: h.texto || h.tipo, cantidad: h.cantidad?.valor || 1, notas: '' })),
        ...(d.uniones || []).map((u) => mk('union', u.x, u.y, { descripcion: u.entre || 'Unión entre paneles', angulo: u.angulo?.estado === 'confirmado' && u.angulo?.valor ? `${u.angulo.valor}°` : '', notas: u.angulo?.estado !== 'confirmado' ? '⚠ ángulo pendiente de confirmar' : '' })),
      ]);
      const nueva = {
        nombre: `Pizarra ${d.cliente?.valor || new Date().toLocaleDateString('es-GT')}`.slice(0, 60),
        cliente_nombre: d.cliente?.valor || '', imagen: data.imagen || imagen, descripcion: descNueva,
        interpretacion: d, elementos, trazos: [], gastos: [], rotacion: 0, estado: 'borrador',
      };
      const { data: creada } = await api.post('/sa/pizarra', nueva);
      setPiz(creada);
      savedRef.current = JSON.stringify(bodyAuto(creada));
      resetHist(creada); setSel(null); setVista('foto'); setSelPanel(null); setDescNueva('');
      toast.success(`Interpretación lista: ${elementos.length} elementos marcados — confírmalos uno a uno`, { duration: 6000 });
      if ((d.preguntas || []).length) toast.info(`El asistente tiene ${d.preguntas.length} pregunta(s) — revisa el panel`, { duration: 6000 });
    } catch (err) { toast.error(err?.response?.data?.detail || 'No se pudo analizar el dibujo'); }
    setAnalizando(false);
  };

  const abrir = async (id) => {
    try {
      const { data } = await api.get(`/sa/pizarra/${id}`);
      const cargada = { ...data, elementos: reetiquetar(data.elementos || []) };
      setPiz(cargada);
      savedRef.current = JSON.stringify(bodyAuto(cargada));
      resetHist(cargada); setSel(null); setVista('foto'); setSelPanel(null);
    } catch { toast.error('No se pudo abrir la pizarra'); }
  };

  const guardar = async (silencioso = false) => {
    if (!piz?.id) return;
    setGuardando(true);
    try {
      const { id, vendor_id, vendor_name, created_at, updated_at, ...body } = piz;
      await api.put(`/sa/pizarra/${piz.id}`, body);
      savedRef.current = JSON.stringify(bodyAuto(piz));
      setAutosave('guardado');
      if (!silencioso) toast.success('Pizarra guardada — puedes continuar después');
    } catch (e) { toast.error(e?.response?.data?.detail || 'Error al guardar'); }
    setGuardando(false);
  };

  const patchEl = (id, patch) => {
    let els = piz.elementos.map((e) => (e.id === id ? { ...e, ...patch } : e));
    if (patch.tipo) els = reetiquetar(els);
    mutar({ elementos: els });
  };
  const addEl = (x, y) => {
    if (porColocar) {
      mutar({ elementos: piz.elementos.map((e) => (e.id === porColocar ? { ...e, x, y, sin_ubicar: false } : e)) });
      setSel(porColocar);
      setPorColocar(null);
      setHerramienta('seleccionar');
      toast.success('Elemento ubicado en el dibujo ✅');
      return;
    }
    const nuevo = { id: Math.random().toString(36).slice(2, 10), tipo: tipoNuevo || 'otro',
      x: vista === 'plano' ? null : x, y: vista === 'plano' ? null : y,
      ...(vista === 'plano' ? { px: x, py: y } : {}),
      etiqueta: '', estado: 'pendiente', origen: 'manual', props: { cantidad: 1 }, producto: null };
    mutar({ elementos: reetiquetar([...piz.elementos, nuevo]) });
    setSel(nuevo.id);
    setHerramienta('seleccionar');
    toast.info(tipoNuevo && tipoNuevo !== 'otro' ? 'Completa los datos en la tarjeta' : '¿Qué elemento acabas de agregar? Elígelo en la tarjeta');
    setTipoNuevo(null);
  };
  const clampN = (v, a, b) => Math.min(Math.max(v, a), b);
  // kind 'ancla' mueve el punto físico; por defecto mueve la etiqueta visual. Campos según vista (foto/plano)
  const moverEl = (id, x, y, kind) => setPiz((p) => ({
    ...p,
    elementos: p.elementos.map((e) => {
      if (e.id !== id) return e;
      if (vista === 'plano') {
        return kind === 'ancla' ? { ...e, px: clampN(x, 0.0, 1.0), py: clampN(y, 0.0, 1.0) } : { ...e, plx: clampN(x, -0.12, 1.12), ply: clampN(y, -0.12, 1.12) };
      }
      return kind === 'ancla' ? { ...e, x: clampN(x, 0.01, 0.99), y: clampN(y, 0.01, 0.99) } : { ...e, lx: clampN(x, -0.12, 1.12), ly: clampN(y, -0.12, 1.12) };
    }),
  }));
  const finMover = () => commit(pizRef.current); // el arrastre entra al historial al soltar
  const dupEl = (id) => {
    const o = piz.elementos.find((e) => e.id === id);
    const nu = { ...o, id: Math.random().toString(36).slice(2, 10), x: Math.min((o.x ?? 0.5) + 0.05, 0.97), y: Math.min((o.y ?? 0.5) + 0.05, 0.97),
      lx: o.lx != null ? Math.min(o.lx + 0.05, 1.1) : undefined, ly: o.ly != null ? Math.min(o.ly + 0.05, 1.1) : undefined, sin_ubicar: false };
    mutar({ elementos: reetiquetar([...piz.elementos, nu]) });
    setSel(nu.id);
  };
  const delEl = (id) => { mutar({ elementos: reetiquetar(piz.elementos.filter((e) => e.id !== id)) }); setSel(null); };

  const pendientes = (piz?.elementos || []).filter((e) => e.estado !== 'confirmado');
  const generarPresupuesto = async () => {
    const geo = piz.elementos.filter((e) => ['vidrio', 'puerta'].includes(e.tipo) && e.estado === 'confirmado');
    if (!geo.length) return toast.error('Confirma al menos un vidrio o puerta con medidas antes de generar');
    setGenerando(true);
    try {
      const herr = piz.elementos.filter((e) => !['vidrio', 'puerta', 'union', 'anotacion'].includes(e.tipo));
      const gastosTxt = (piz.gastos || []).map((g) => `• ${g.concepto}${g.descripcion ? ` (${g.descripcion})` : ''}: ${g.cantidad}×${g.factor} ${g.unidad} ${g.pendiente_precio ? '[PRECIO PENDIENTE]' : `= Q${g.subtotal}`}`).join('\n');
      const herrTxt = herr.map((e) => `• ${e.etiqueta} ${e.props?.descripcion || e.tipo} ×${e.props?.cantidad || 1}${e.producto ? ` → ${e.producto.codigo} ${e.producto.nombre}${e.producto.propuesto ? ' [PROPUESTO — pendiente aprobación]' : ''}` : ' [SIN PRODUCTO]'}`).join('\n');
      const pend = pendientes.map((e) => `${e.etiqueta} ${e.tipo}`).join(', ');
      const notas = ['[CREADO DESDE PIZARRA INTELIGENTE — revisar en Modulador antes de calcular]',
        herrTxt && `HERRAJES MARCADOS EN LA PIZARRA:\n${herrTxt}`,
        gastosTxt && `GASTOS EXTRAS Y SERVICIOS (ver pizarra):\n${gastosTxt}`,
        pend && `⚠️ PENDIENTES DE CONFIRMAR: ${pend}`].filter(Boolean).join('\n\n');
      const { data: pj } = await api.post('/sa/proyectos', {
        nombre: `Pizarra — ${piz.cliente_nombre || piz.nombre}`.slice(0, 80),
        cliente_nombre: piz.cliente_nombre || '', notas });
      for (const el of geo) {
        const fam = el.tipo === 'puerta' ? 'pabm' : 'vtf';
        const receta = {
          familia: fam, vano_ancho: Math.round(Number(el.props.ancho_m) * 1000), vano_alto: Math.round(Number(el.props.alto_m) * 1000),
          apertura: el.props.apertura || 'derecha', embisagrado: 'muro',
          vidrio: { tipo: el.props.tipo_vidrio || '', espesor: el.props.espesor || '', color: el.props.color || '' },
          fijos: [], despiece_estado: 'pendiente',
        };
        const hv = herr.filter((h) => h.producto && !h.producto.propuesto).map((h, k) => ({
          hid: `h_piz_${k}`, sku: h.producto.codigo, nombre: h.producto.nombre, unidad: h.producto.unidad || 'unidad',
          cantidad: Number(h.props?.cantidad) || 1, acabado: h.producto.acabado || '', costo: 0,
          observaciones: `Marcador ${h.etiqueta} de la Pizarra`, modulo_id: '', union_id: '',
          x: 200 + k * 60, y: 300, pos_rel: 'libre', orientacion: 'vertical', etiqueta: h.etiqueta,
        }));
        if (el === geo[0] && hv.length) receta.herrajes_visuales = hv;
        const dis = derivarDiseno(receta, fam);
        await api.post(`/sa/proyectos/${pj.id}/elementos`, {
          familia: fam, nombre: `${el.etiqueta} — ${el.props.descripcion || (fam === 'pabm' ? 'Puerta' : 'Vidrio fijo')} (pizarra)`.slice(0, 60),
          cantidad: 1, receta, diseno: dis, despiece: [], descripcion: el.props.notas || '' });
      }
      if ((piz.gastos || []).length) {
        try {
          const { data: ga } = await api.post(`/sa/pizarra/${piz.id}/gastos-a-proyecto`, { proyecto_id: pj.id });
          toast.success(`${ga.agregados} gasto(s) sumados al resumen financiero del presupuesto${ga.pendientes?.length ? ` · ${ga.pendientes.length} con tarifa pendiente (no suman)` : ''}`, { duration: 7000 });
        } catch { toast.warning('Los gastos no se pudieron sumar al proyecto — revisa las tarifas'); }
      }
      setPiz({ ...piz, proyecto_id: pj.id, estado: 'con_presupuesto' });
      await api.put(`/sa/pizarra/${piz.id}`, { ...piz, proyecto_id: pj.id, estado: 'con_presupuesto', imagen: '' });
      toast.success(`Borrador SA ${pj.numero || ''} creado — los cálculos y PDF usan el motor existente`, { duration: 8000 });
      navigate(`/presupuestos-sa/${pj.id}`);
    } catch (e) { toast.error(e?.response?.data?.detail || 'No se pudo generar el presupuesto'); }
    setGenerando(false);
  };

  const imprimir = () => {
    const filas = piz.elementos.filter((e) => e.tipo !== 'anotacion').map((e) =>
      `<tr><td>${e.etiqueta}</td><td>${e.tipo}</td><td>${e.producto?.codigo || '—'}</td><td>${e.producto?.nombre || e.props?.descripcion || ''}</td><td>${e.props?.cantidad || 1}</td><td>${e.producto?.precio ? 'Q' + e.producto.precio : 'Pendiente'}</td><td>${e.estado}</td></tr>`).join('');
    const gastos = (piz.gastos || []).map((g) => `<tr><td colspan="3">${g.concepto} ${g.descripcion || ''}</td><td>${g.cantidad}×${g.factor} ${g.unidad}</td><td colspan="2">${g.pendiente_precio ? 'Precio pendiente' : 'Q' + g.subtotal}</td><td></td></tr>`).join('');
    const w = window.open('', '_blank');
    w.document.write(`<html><head><title>${piz.nombre}</title><style>body{font-family:sans-serif;color:#1F1B29}h1{color:#38205F;font-size:18px}table{width:100%;border-collapse:collapse;font-size:11px}td,th{border:1px solid #B9A5D9;padding:4px}th{background:#38205F;color:#fff}img{max-width:100%;max-height:420px}</style></head><body><h1>PIZARRA INTELIGENTE SACH — ${piz.nombre}</h1><p>Cliente: ${piz.cliente_nombre || '—'} · Vendedor: ${piz.vendor_name || ''}</p><img src="${piz.imagen}"/><h1>Planilla</h1><table><tr><th>#</th><th>Elemento</th><th>Código</th><th>Descripción</th><th>Cant.</th><th>Precio aut.</th><th>Estado</th></tr>${filas}${gastos}</table></body></html>`);
    w.document.close();
    setTimeout(() => w.print(), 400);
  };

  const elSel = piz?.elementos?.find((e) => e.id === sel);

  // ── Vista lista ──
  if (!piz) return (
    <div className="max-w-3xl mx-auto space-y-4 p-1" data-testid="pizarra-home">
      <div>
        <h1 className="text-xl font-black text-[#1F1B29]">🧠 Pizarra Inteligente SACH</h1>
        <p className="text-xs text-[#6B6B6B]">Toma una foto de un dibujo a mano, la IA lo interpreta y trabajas sobre él hasta generar el presupuesto. Las otras 3 modalidades siguen igual.</p>
      </div>
      <input ref={fileRef} type="file" accept="image/jpeg,image/png,image/webp,application/pdf" className="hidden" onChange={elegir} data-testid="pizarra-file-input" />
      <input ref={camRef} type="file" accept="image/*" capture="environment" className="hidden" onChange={elegir} data-testid="pizarra-cam-input" />
      {analizando ? (
        <div className="border-2 border-[#7352B5]/40 rounded-[14px] p-10 text-center" data-testid="pizarra-analizando">
          <Spinner size={34} className="animate-spin text-[#7352B5] mx-auto" />
          <p className="text-sm font-black text-[#38205F] mt-2">Interpretando tu dibujo con IA…</p>
          <p className="text-[11px] text-[#6B6B6B]">La IA solo lee lo que se ve — nada se calcula sin tu confirmación.</p>
        </div>
      ) : (
        <div className="space-y-2.5">
          <textarea value={descNueva} onChange={(e) => setDescNueva(e.target.value)} rows={2} maxLength={400}
            placeholder="Descripción opcional para la IA — ej. «Mampara de baño en esquina, vidrio templado 10 mm, puerta a la derecha, fijo lateral, herrajes negros»"
            className="w-full text-[12px] border border-[#B9A5D9]/60 rounded-[12px] p-2.5 outline-none focus:border-[#7352B5] bg-white resize-none" data-testid="pizarra-desc-nueva" />
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <button onClick={() => camRef.current?.click()} className="border-2 border-dashed border-[#7352B5]/50 bg-[#F4EFFB] rounded-[14px] p-8 flex flex-col items-center gap-2 text-[#38205F] hover:bg-[#B9A5D9]/30" data-testid="pizarra-tomar-foto">
          <Camera size={38} weight="bold" /><span className="text-sm font-black">Tomar foto o adjuntar dibujo</span><span className="text-[10px]">Cámara del celular</span>
          </button>
          <button onClick={() => fileRef.current?.click()} className="border-2 border-dashed border-[#B9A5D9] rounded-[14px] p-8 flex flex-col items-center gap-2 text-[#6B6B6B] hover:border-[#7352B5] hover:text-[#38205F]" data-testid="pizarra-subir-archivo">
            <FileArrowUp size={38} /><span className="text-sm font-black">Subir desde galería / archivos</span><span className="text-[10px]">JPG · JPEG · PNG · PDF</span>
          </button>
          </div>
        </div>
      )}
      <div className="space-y-2" data-testid="pizarra-lista">
        <div className="flex items-center justify-between">
          <p className="text-[10px] font-black uppercase text-[#6B6B6B]">Mis pizarras guardadas</p>
          {(user?.role === 'admin') && <button onClick={() => setVerTarifas(true)} className="text-[10px] font-black text-[#7352B5] underline" data-testid="pizarra-tarifas-btn">⚙ Tarifas de servicios</button>}
        </div>
        {(lista || []).map((pz) => (
          <button key={pz.id} onClick={() => abrir(pz.id)} className="w-full text-left border border-[#B9A5D9] rounded-[12px] px-3 py-2 hover:bg-[#F4EFFB] flex justify-between items-center" data-testid={`pizarra-item-${pz.id}`}>
            <span><span className="text-xs font-black text-[#1F1B29] block">{pz.nombre}</span>
              <span className="text-[10px] text-[#6B6B6B]">{pz.cliente_nombre || 'Sin cliente'} · {pz.vendor_name} · {(pz.elementos || []).length} elementos {pz.proyecto_id ? <span className="text-[#D5AD65] font-black">· 📄 con presupuesto</span> : ''}</span></span>
            <span className="text-[#7352B5] font-black text-xs">Abrir →</span>
          </button>
        ))}
        {lista && lista.length === 0 && <p className="text-[11px] text-[#6B6B6B] italic">Aún no tienes pizarras.</p>}
      </div>
      {verTarifas && <TarifasServicios onClose={() => setVerTarifas(false)} />}
    </div>
  );

  // ── Editor ──
  const prepAdd = (t) => { setTipoNuevo(t); setHerramienta('agregar'); setMenu(null); toast.info('Toca el dibujo donde va el elemento'); };
  const itemsAgregar = [
    ['🪟 Vidrio / panel', () => prepAdd('vidrio'), 'pizarra-add-vidrio'],
    ['🚪 Puerta', () => prepAdd('puerta'), 'pizarra-add-puerta'],
    ['⚙️ Herraje (bisagra, clip…)', () => prepAdd('otro'), 'pizarra-add-herraje'],
    ['📝 Anotación', () => prepAdd('anotacion'), 'pizarra-add-anotacion'],
    ['✏️ Trazo libre', () => { setHerramienta('dibujar'); setMenu(null); }, 'pizarra-tool-dibujar'],
  ];
  const itemsEditar = [
    ['👆 Seleccionar / abrir tarjeta', () => { setHerramienta('seleccionar'); setMenu(null); }, 'pizarra-tool-seleccionar'],
    ['✋ Mover marcadores', () => { setHerramienta('mover'); setMenu(null); }, 'pizarra-tool-mover'],
    ['📐 Medir con regla', () => { setHerramienta('medir'); setMenu(null); }, 'pizarra-tool-medir'],
    ['🧹 Quitar medidas', () => { mutar({ mediciones: [] }); setMenu(null); }, 'pizarra-limpiar-medidas', !(piz.mediciones || []).length],
    ['🗑 Eliminar seleccionado', () => { delEl(sel); setMenu(null); }, 'pizarra-eliminar-sel', !sel],
  ];
  const itemsMas = [
    ['⟳ Girar imagen 90°', () => setPiz({ ...piz, rotacion: ((piz.rotacion || 0) + 90) % 360 }), 'pizarra-girar'],
    [verMarcadores ? '🙈 Ocultar marcadores' : '👁 Mostrar marcadores', () => { setVerMarcadores((v) => !v); setMenu(null); }, 'pizarra-toggle-marcadores'],
    ['🧲 Organizar marcadores', () => organizarMarcadores(), 'pizarra-organizar'],
    ['📝 Descripción para la IA', () => { setDescTmp(piz.descripcion || ''); setEditDesc(true); setMenu(null); }, 'pizarra-editar-desc'],
    ['🖨 Vista imprimible', () => { setMenu(null); imprimir(); }, 'pizarra-imprimir'],
    ...(esEscritorio ? [] : [['📄 Generar presupuesto SA', () => { setMenu(null); generarPresupuesto(); }, 'pizarra-generar']]),
  ];
  // Sugiere posiciones libres alrededor de la imagen manteniendo anclajes; solo vista previa hasta confirmar
  const organizarMarcadores = () => {
    setMenu(null);
    const M = 0.08, SEP = 0.09;
    const els = piz.elementos.filter((e) => !e.sin_ubicar && e.x != null);
    if (!els.length) return;
    const base = els.map((e) => {
      const dx = (e.x - 0.5) || 0.0001, dy = (e.y - 0.5) || 0.0001;
      const h = Math.hypot(dx, dy), cos = dx / h, sin = dy / h;
      const tx = (cos > 0 ? 0.5 + M : -0.5 - M) / cos;
      const ty = (sin > 0 ? 0.5 + M : -0.5 - M) / sin;
      const t = Math.min(tx, ty);
      return { id: e.id, lx: 0.5 + cos * t, ly: 0.5 + sin * t, lado: t === tx ? (cos > 0 ? 'der' : 'izq') : (sin > 0 ? 'abj' : 'arr') };
    });
    ['izq', 'der'].forEach((lado) => {
      const g = base.filter((b) => b.lado === lado).sort((a, b) => a.ly - b.ly);
      g.forEach((b, i) => { if (i && b.ly - g[i - 1].ly < SEP) b.ly = g[i - 1].ly + SEP; b.ly = clampN(b.ly, -M, 1 + M); });
    });
    ['arr', 'abj'].forEach((lado) => {
      const g = base.filter((b) => b.lado === lado).sort((a, b) => a.lx - b.lx);
      g.forEach((b, i) => { if (i && b.lx - g[i - 1].lx < SEP) b.lx = g[i - 1].lx + SEP; b.lx = clampN(b.lx, -M, 1 + M); });
    });
    setPreviewOrg(Object.fromEntries(base.map((b) => [b.id, { lx: b.lx, ly: b.ly }])));
    toast.info('Vista previa de organización — confirma para aplicar');
  };
  const aplicarOrg = () => {
    mutar({ elementos: piz.elementos.map((e) => (previewOrg[e.id] ? { ...e, lx: previewOrg[e.id].lx, ly: previewOrg[e.id].ly } : e)) });
    setPreviewOrg(null);
    toast.success('Marcadores organizados — los anclajes no cambiaron');
  };
  // Croquis → Plano Inteligente (geometría estructurada; el sistema dibuja el SVG; elementos/productos intactos)
  const generarPlano = async () => {
    setGenPlano(true);
    try {
      const { data } = await api.post(`/sa/pizarra/${piz.id}/generar-plano`, {}, { timeout: 200000 });
      mutar({ plano: data.plano });
      setVista('plano'); setSelPanel(null); setSel(null);
      toast.success('Plano generado — compáralo con el croquis y confirma la interpretación');
    } catch (e) { toast.error(e?.response?.data?.detail || 'No se pudo generar el plano'); }
    setGenPlano(false);
  };
  const MenuPop = ({ items, arriba, der }) => (
    <>
      <div className="fixed inset-0 z-40" onClick={() => setMenu(null)} />
      <div className={`absolute ${arriba ? 'bottom-full mb-2' : 'top-full mt-1.5'} ${der ? 'right-0' : 'left-0'} bg-white border border-[#B9A5D9]/70 rounded-[12px] shadow-2xl py-1 min-w-[200px] z-50`}>
        {items.map(([l, fn, tid, dis]) => (
          <button key={tid} type="button" disabled={dis} onClick={fn} data-testid={tid}
            className="w-full text-left px-3.5 py-2.5 text-[12px] font-bold text-[#38205F] hover:bg-[#F4EFFB] disabled:opacity-40">{l}</button>
        ))}
      </div>
    </>
  );
  const panelMateriales = (
    <>
      <PlanillaPizarra elementos={piz.elementos} seleccionado={sel} onSelect={(id) => { setSel(id); setFocus({ id, tick: Date.now() }); setVerMateriales(false); }} />
      <GastosPizarra gastos={piz.gastos} onChange={(g) => setPiz({ ...piz, gastos: g })} />
      <p className="text-[10px] text-[#6B6B6B]" data-testid="pizarra-resumen-estado">
        {pendientes.length ? `🟠 ${pendientes.length} elemento(s) pendientes de confirmar — el presupuesto se mantiene como borrador.` : '🟢 Todos los elementos confirmados.'}
      </p>
    </>
  );
  const configSel = elSel ? (
    <ConfigElemento elemento={elSel} pizarraId={piz.id} mediciones={piz.mediciones || []} onPatch={patchEl} onEliminar={delEl} onDuplicar={dupEl} onCerrar={() => setSel(null)} />
  ) : null;

  return (
    <div className="space-y-3 p-1" data-testid="pizarra-editor">
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 flex-wrap">
            <button onClick={() => { guardar(true); setPiz(null); cargarLista(); }} className="text-[10px] font-black text-[#7352B5]" data-testid="pizarra-volver">← Mis pizarras</button>
            <span className={`text-[9px] font-black ${autosave === 'guardado' ? 'text-emerald-600' : autosave === 'error' ? 'text-red-500' : 'text-[#6B6B6B]'}`} data-testid="pizarra-autosave">
              {autosave === 'guardado' ? '✓ Guardado' : autosave === 'guardando' ? '⟳ Guardando…' : autosave === 'error' ? '⚠ Sin conexión — reintentando' : '● Cambios sin guardar'}
            </span>
            {!esEscritorio && <span className="text-[9px] font-black text-[#7352B5] bg-[#F4EFFB] border border-[#B9A5D9]/50 rounded-full px-2 py-0.5" data-testid="pizarra-herramienta-activa">{TOOL_LABEL[herramienta]}</span>}
          </div>
          <Input value={piz.nombre} onChange={(e) => setPiz({ ...piz, nombre: e.target.value })} className="h-8 text-sm font-black border-0 px-0 bg-transparent" data-testid="pizarra-nombre" />
        </div>
        <div className="flex gap-1.5 flex-wrap items-center">
          <Button size="sm" variant="outline" onClick={() => moverHist(-1)} disabled={!puedeDeshacer} className="h-9" data-testid="pizarra-deshacer"><ArrowUUpLeft size={15} /></Button>
          <Button size="sm" variant="outline" onClick={() => moverHist(1)} disabled={!puedeRehacer} className="h-9" data-testid="pizarra-rehacer"><ArrowUUpRight size={15} /></Button>
          {esEscritorio && (
            <>
              <Button size="sm" onClick={() => guardar()} disabled={guardando} className="h-9 bg-[#38205F] hover:bg-[#2b1849] text-white text-xs font-bold" data-testid="pizarra-guardar">{guardando ? '…' : '💾 Guardar borrador'}</Button>
              <Button size="sm" onClick={generarPresupuesto} disabled={generando} className="h-9 bg-[#D5AD65] hover:bg-[#c89b4f] text-[#38205F] text-xs font-black" data-testid="pizarra-generar">
                {generando ? <Spinner size={14} className="animate-spin" /> : '📄 Generar presupuesto SA'}
              </Button>
            </>
          )}
        </div>
      </div>

      {esEscritorio && (
        <div className="flex gap-1.5 flex-wrap items-center" data-testid="pizarra-toolbar">
          {[['agregar', '➕ Agregar', itemsAgregar], ['editar', '✏️ Editar', itemsEditar], ['mas', '⋯ Más', itemsMas]].map(([k, l, items]) => (
            <div key={k} className="relative">
              <button type="button" onClick={() => setMenu(menu === k ? null : k)}
                className={`text-[11px] font-bold px-3 py-1.5 rounded-full border ${menu === k ? 'bg-[#7352B5] text-white border-[#7352B5]' : 'bg-white text-[#38205F] border-[#B9A5D9]/70'}`}
                data-testid={`pizarra-menu-${k}`}>{l} ▾</button>
              {menu === k && <MenuPop items={items} />}
            </div>
          ))}
          <span className="text-[10px] font-black text-[#7352B5] bg-[#F4EFFB] border border-[#B9A5D9]/50 rounded-full px-2.5 py-1" data-testid="pizarra-herramienta-activa">Herramienta: {TOOL_LABEL[herramienta]}</span>
          <span className="text-[9px] text-[#6B6B6B]">🟣 pendiente · 🟢 confirmado · 🟠 incompleto · 🔴 error</span>
        </div>
      )}
      {herramienta === 'medir' && (
        <p className="text-[10px] text-amber-700 bg-amber-50 border border-amber-300 rounded-lg px-2.5 py-1.5" data-testid="pizarra-medir-hint">
          📐 Traza una línea sobre una medida CONOCIDA del dibujo y escribe sus metros (calibración, verde). Las siguientes líneas muestran una <b>estimación visual (~, ámbar)</b> — nunca sustituyen la medida real confirmada.
        </p>
      )}

      {vista === 'foto' && (piz.interpretacion?.preguntas || []).length > 0 && (
        <div className="border border-amber-400/60 bg-amber-50 rounded-[12px] p-2.5" data-testid="pizarra-preguntas">
          <p className="text-[10px] font-black uppercase text-amber-600">🤖 El asistente necesita confirmar:</p>
          {piz.interpretacion.preguntas.slice(0, 5).map((q, i) => <p key={i} className="text-[11px] text-[#1F1B29]">• {q}</p>)}
        </div>
      )}

      {vista === 'foto' && (piz.elementos || []).some((e) => e.sin_ubicar) && (
        <div className="border border-[#7352B5]/40 bg-[#F4EFFB] rounded-[12px] p-2.5" data-testid="pizarra-por-ubicar">
          <p className="text-[10px] font-black uppercase text-[#38205F]">📍 Elementos por ubicar (la IA no determinó su posición — no se inventa):</p>
          <div className="flex flex-wrap gap-1.5 mt-1">
            {piz.elementos.filter((e) => e.sin_ubicar).map((e) => (
              <button key={e.id} onClick={() => { setPorColocar(e.id); setHerramienta('agregar'); toast.info(`Toca el dibujo donde va ${e.etiqueta} (${e.props?.descripcion || e.tipo})`); }}
                className={`text-[10px] font-black px-2.5 py-1.5 rounded-full border ${porColocar === e.id ? 'bg-[#7352B5] text-white border-[#7352B5]' : 'bg-white text-[#38205F] border-[#7352B5]/40'}`}
                data-testid={`por-ubicar-${e.etiqueta}`}>
                {e.etiqueta} · {(e.props?.descripcion || e.tipo).slice(0, 24)}
              </button>
            ))}
          </div>
        </div>
      )}

      {previewOrg && (
        <div className="flex items-center gap-2 flex-wrap border border-[#D5AD65] bg-[#FDF8EE] rounded-[12px] px-2.5 py-2" data-testid="pizarra-organizar-banner">
          <span className="text-[11px] font-black text-[#38205F]">🧲 Vista previa de organización — los anclajes y productos NO cambian. ¿Aplicar?</span>
          <button onClick={aplicarOrg} className="text-[11px] font-black text-white bg-[#38205F] rounded-lg px-3 py-1.5" data-testid="pizarra-organizar-aplicar">✓ Aplicar</button>
          <button onClick={() => setPreviewOrg(null)} className="text-[11px] font-bold text-[#6B6B6B] border border-[#B9A5D9]/60 rounded-lg px-3 py-1.5" data-testid="pizarra-organizar-cancelar">✕ Cancelar</button>
        </div>
      )}

      <div className="flex items-center gap-2 flex-wrap">
        <div className="flex rounded-full border border-[#B9A5D9]/70 overflow-hidden bg-white">
          <button type="button" onClick={() => { setVista('foto'); setSelPanel(null); setHerramienta('seleccionar'); }}
            className={`text-[11px] font-black px-3 py-1.5 ${vista === 'foto' ? 'bg-[#38205F] text-white' : 'text-[#38205F]'}`} data-testid="pizarra-vista-foto">📷 Dibujo original</button>
          <button type="button" onClick={() => { if (piz.plano) { setVista('plano'); setSel(null); setHerramienta('seleccionar'); } }} disabled={!piz.plano}
            className={`text-[11px] font-black px-3 py-1.5 disabled:opacity-40 ${vista === 'plano' ? 'bg-[#38205F] text-white' : 'text-[#38205F]'}`} data-testid="pizarra-vista-plano">📐 Plano inteligente</button>
        </div>
        {!piz.plano && (
          <button type="button" onClick={generarPlano} disabled={genPlano}
            className="text-[11px] font-black px-3 py-1.5 rounded-full bg-[#D5AD65] hover:bg-[#c89b4f] text-[#38205F] disabled:opacity-60" data-testid="pizarra-generar-plano">
            {genPlano ? '⏳ Generando plano…' : '✨ Generar plano inteligente'}
          </button>
        )}
        {vista === 'plano' && piz.plano && (confirmRegen ? (
          <span className="flex items-center gap-1 text-[10px] font-black">
            <span className="text-amber-700">¿Reemplazar el plano actual (se pierden correcciones)?</span>
            <button type="button" onClick={() => { setConfirmRegen(false); generarPlano(); }} className="px-2.5 py-1.5 rounded-lg bg-amber-500 text-white" data-testid="pizarra-regenerar-si">Sí</button>
            <button type="button" onClick={() => setConfirmRegen(false)} className="px-2.5 py-1.5 rounded-lg border border-[#B9A5D9]/60 text-[#6B6B6B]" data-testid="pizarra-regenerar-no">No</button>
          </span>
        ) : (
          <button type="button" onClick={() => setConfirmRegen(true)} disabled={genPlano}
            className="text-[10px] font-bold px-2.5 py-1.5 rounded-full border border-[#B9A5D9]/70 text-[#38205F] disabled:opacity-60" data-testid="pizarra-regenerar-plano">
            {genPlano ? '⏳…' : '♻ Regenerar'}
          </button>
        ))}
        <button type="button" onClick={() => setVerBiblioteca(true)}
          className="text-[10px] font-bold px-2.5 py-1.5 rounded-full border border-[#7352B5]/50 text-[#38205F] bg-white hover:bg-[#F4EFFB]" data-testid="pizarra-biblioteca-btn">📚 Biblioteca</button>
        {vista === 'plano' && <span className="text-[9px] text-[#6B6B6B]">Representación para cotización — no plano de fabricación</span>}
      </div>

      {vista === 'plano' && piz.plano && !piz.plano.confirmado && (
        <div className="border border-[#D5AD65] bg-[#FDF8EE] rounded-[12px] px-2.5 py-2 space-y-1" data-testid="pizarra-plano-banner">
          <p className="text-[11px] font-black text-[#38205F]">
            📋 Interpretación: {(piz.plano.paneles || []).length} panel(es), {(piz.plano.paneles || []).filter((q) => q.tipo === 'puerta').length} puerta(s) ·
            medidas {(piz.plano.paneles || []).filter((q) => q.medida_estado === 'confirmada').length} confirmadas / {(piz.plano.paneles || []).filter((q) => q.medida_estado !== 'confirmada').length} pendientes ·
            herrajes detectados: {(piz.plano.herrajes || []).length}
          </p>
          {piz.plano.resumen && <p className="text-[10px] text-[#6B6B6B]">{piz.plano.resumen}</p>}
          {(piz.plano.dudas || []).slice(0, 4).map((q, i) => <p key={i} className="text-[10px] text-amber-700">⚠ {q}</p>)}
          <div className="flex gap-2 pt-0.5 flex-wrap">
            <button onClick={() => mutar({ plano: { ...piz.plano, confirmado: true } })} className="text-[11px] font-black text-white bg-[#38205F] rounded-lg px-3 py-1.5" data-testid="pizarra-plano-confirmar">✓ Confirmar interpretación</button>
            <span className="text-[9px] text-[#6B6B6B] self-center">Compara con el croquis (📷) y corrige tocando un panel. Los pendientes quedan en ámbar.</span>
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-5 gap-3">
        <div className="lg:col-span-3 h-[56vh] lg:h-[68vh]">
          <LienzoPizarra imagen={piz.imagen} elementos={piz.elementos} trazos={piz.trazos} mediciones={piz.mediciones || []} seleccionado={sel}
            herramienta={herramienta} rotacion={piz.rotacion || 0} focusReq={focus}
            verMarcadores={verMarcadores} previewOrg={previewOrg}
            vista={vista} plano={piz.plano || null} selPanel={selPanel}
            onSelectPanel={(id) => { setSelPanel(id); if (id) setSel(null); }}
            dialogo={elSel && !elSel.sin_ubicar ? configSel : null}
            onSelect={(id) => { setSel(id); if (id) setSelPanel(null); }} onAdd={addEl} onMove={moverEl} onMoveEnd={finMover}
            onMediciones={(m) => mutar({ mediciones: m })}
            onTrazo={(points) => mutar({ trazos: [...piz.trazos, { points }] })} />
        </div>
        {esEscritorio && (
          <div className="lg:col-span-2 space-y-3 lg:max-h-[68vh] lg:overflow-y-auto">
            {vista === 'plano' && selPanel && piz.plano ? (
              <div className="border-2 border-[#D5AD65]/60 rounded-[12px] p-3 bg-white">
                <PanelEditor plano={piz.plano} panelId={selPanel} onChange={(pl) => mutar({ plano: pl })} onCerrar={() => setSelPanel(null)} />
              </div>
            ) : elSel?.sin_ubicar ? (
              <div className="border-2 border-[#7352B5]/40 rounded-[12px] p-3 bg-white">{configSel}</div>
            ) : (
              <div className="border border-[#B9A5D9]/60 rounded-[12px] p-3 text-[11px] text-[#6B6B6B]" data-testid="pizarra-asistente-idle">
                🤖 <b className="text-[#38205F]">Asistente:</b> toca un marcador para abrir su tarjeta (puedes arrastrarla por el encabezado). Arrastra el marcador para llevarlo a un espacio libre; el punto pequeño es su anclaje y también se mueve.
              </div>
            )}
            {panelMateriales}
          </div>
        )}
      </div>

      {!esEscritorio && (
        <div className="sticky bottom-1 z-40 bg-white/95 backdrop-blur border border-[#B9A5D9]/60 rounded-[16px] shadow-xl flex pr-12" style={{ paddingBottom: 'max(env(safe-area-inset-bottom), 2px)' }} data-testid="pizarra-barra-movil">
          {[
            ['agregar', '➕', 'Agregar', null, itemsAgregar, false, 'pizarra-menu-agregar'],
            ['editar', '✏️', 'Editar', null, itemsEditar, false, 'pizarra-menu-editar'],
            ['materiales', '📋', 'Materiales', () => setVerMateriales(true), null, false, 'pizarra-menu-materiales'],
            ['guardar', '💾', 'Guardar', () => guardar(), null, false, 'pizarra-guardar'],
            ['mas', '⋯', 'Más', null, itemsMas, true, 'pizarra-menu-mas'],
          ].map(([k, ic, l, accion, items, der, tid]) => (
            <div key={k} className="relative flex-1">
              <button type="button" onClick={accion || (() => setMenu(menu === k ? null : k))}
                className={`w-full flex flex-col items-center gap-0.5 py-1.5 rounded-[14px] ${menu === k ? 'bg-[#F4EFFB]' : ''} text-[#38205F]`}
                data-testid={tid}>
                <span className="text-[17px] leading-none">{ic}</span>
                <span className="text-[9px] font-black">{l}</span>
              </button>
              {items && menu === k && <MenuPop items={items} arriba der={der} />}
            </div>
          ))}
        </div>
      )}

      {!esEscritorio && verMateriales && (
        <div className="fixed inset-0 z-50" data-testid="pizarra-materiales-sheet">
          <div className="absolute inset-0 bg-black/35" onClick={() => setVerMateriales(false)} />
          <div className="absolute inset-x-0 bottom-0 bg-white rounded-t-[18px] shadow-2xl max-h-[78vh] overflow-y-auto p-3 space-y-3">
            <div className="flex justify-between items-center">
              <p className="text-xs font-black text-[#38205F]">📋 Materiales — planilla y gastos</p>
              <button onClick={() => setVerMateriales(false)} className="text-[#6B6B6B] font-black px-2 py-1" data-testid="pizarra-materiales-cerrar">✕</button>
            </div>
            {panelMateriales}
          </div>
        </div>
      )}

      {!esEscritorio && elSel?.sin_ubicar && (
        <div className="fixed inset-x-2 bottom-16 z-50 bg-white border-2 border-[#7352B5]/50 rounded-[14px] shadow-2xl max-h-[62vh] overflow-y-auto p-3" data-testid="pizarra-config-movil">
          {configSel}
        </div>
      )}

      {!esEscritorio && vista === 'plano' && selPanel && piz.plano && (
        <div className="fixed inset-x-2 bottom-16 z-50 bg-white border-2 border-[#D5AD65]/70 rounded-[14px] shadow-2xl max-h-[45vh] overflow-y-auto p-3" data-testid="pizarra-panel-movil">
          <PanelEditor plano={piz.plano} panelId={selPanel} onChange={(pl) => mutar({ plano: pl })} onCerrar={() => setSelPanel(null)} />
        </div>
      )}

      {verBiblioteca && (
        <BibliotecaPlanos planoActual={piz.plano || null} hayPlano={!!piz.plano} user={user}
          onUsar={(plano, nombreD) => {
            mutar({ plano: { ...plano, confirmado: false, dudas: [], generado_en: new Date().toISOString(), generado_por: `Biblioteca — ${nombreD}` } });
            setVista('plano'); setSelPanel(null); setSel(null); setVerBiblioteca(false);
            toast.success(`Diseño «${nombreD}» aplicado — ajusta medidas y confirma la interpretación`, { duration: 6000 });
          }}
          onClose={() => setVerBiblioteca(false)} />
      )}

      {editDesc && (
        <div className="fixed inset-0 z-50" data-testid="pizarra-desc-sheet">
          <div className="absolute inset-0 bg-black/35" onClick={() => setEditDesc(false)} />
          <div className="absolute inset-x-0 bottom-0 bg-white rounded-t-[18px] shadow-2xl p-3 space-y-2">
            <p className="text-xs font-black text-[#38205F]">📝 Descripción para la IA (opcional)</p>
            <textarea value={descTmp} onChange={(e) => setDescTmp(e.target.value)} rows={3} maxLength={400} autoFocus
              placeholder="Ej. «Mampara de baño en esquina, vidrio templado 10 mm, puerta a la derecha, herrajes negros»"
              className="w-full text-[12px] border border-[#B9A5D9]/60 rounded-[12px] p-2.5 outline-none focus:border-[#7352B5] resize-none" data-testid="pizarra-desc-input" />
            <div className="flex gap-2">
              <button onClick={() => { setPiz({ ...piz, descripcion: descTmp }); setEditDesc(false); toast.success('Descripción guardada — la IA la usará al regenerar el plano'); }}
                className="text-[11px] font-black text-white bg-[#38205F] rounded-lg px-4 py-2" data-testid="pizarra-desc-guardar">💾 Guardar</button>
              <button onClick={() => setEditDesc(false)} className="text-[11px] font-bold text-[#6B6B6B] border border-[#B9A5D9]/60 rounded-lg px-4 py-2" data-testid="pizarra-desc-cancelar">Cancelar</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
